import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Bar as UI5Bar, Button, BusyIndicator, Title, Text } from "@ui5/webcomponents-react";
import ManualForm from "./ManualForm";
import SocioeconomicForm from "./SocioeconomicForm";
import SocioeconomicForm2 from "./SocioeconomicForm2";
import SocioeconomicForm3 from "./SocioeconomicForm3";
import SocioeconomicForm4 from "./SocioeconomicForm4";
import SocioeconomicForm5 from "./SocioeconomicForm5";
import SocioeconomicForm6 from "./SocioeconomicForm6";
import SocioeconomicForm7 from "./SocioeconomicForm7";
import EnvironmentSanitationForm from "./EnvironmentSanitationForm";
import FamilyDemographic from "./FamilyDemographic";
import LastPage from "./LastPage";
import UIModel from "../models/UIModel.json"; // Assuming UIModel is still relevant for payload structure
import merge from 'lodash/merge';

const TOTAL_STEPS = 11; // Same as MainFormContainer

// Default health data structure matching FamilyDemographic.js (copied from MainFormContainer)
const defaultHealthDataForMerge = {
  G17: {
    BCG: { received: false, atBirth: false },
    HepatitisB: { received: false, atBirth: false },
    Pentavalent: { received: false, '1.5mos': false, '2.5mos': false, '3.5mos': false },
    OPV: { received: false, '1.5mos': false, '2.5mos': false, '3.5mos': false },
    IPV: { received: false },
    Pneumococcal: { received: false, '1.5mos': false, '2.5mos': false, '3.5mos': false },
    MMR: { received: false, '9mos': false, '1year': false },
    Other: { received: false, specify: '' }
  },
  G18: { status: '', date: '' },
  G19: {
    VitaminA: { received: false, date: '' },
    Iron: { received: false, date: '' },
    MNP: { received: false },
    IronFortifiedRice: { received: false },
    IodizedSalt: { received: false, source: '', sourceOther: '' }
  },
  G20: {
    Month1: false, Month2: false, Month3: false, Month4: false, Month5: false, Month6: false,
    DateLastBreastfed: ''
  },
  G21: '',
};


// Initial form data structure (copied and adapted from MainFormContainer)
// This will be overwritten by fetched data
const initialDetailFormData = {
  householdType: "Family",
  longitude: "",
  latitude: "",
  houseNo: "",
  street: "",
  purokSitio: "",
  barangay: "",
  cityMunicipality: "",
  province: "",
  municipality: "", // Note: cityMunicipality and municipality might be redundant depending on your data model
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  familyType: "",
  headID: "",
  status: "Active",
  sex: "",
  gender: "",
  civilStatus: "",
  numFamilyMembers: "",
  yearMoved: "",
  prevLocation: "",
  mobileProvider: ["", "", ""],
  contactNumber: ["", "", ""],
  internetProvider: "",
  internetProviderOther: "",
  householdAppliances: {},
  ictDevices: {},
  vehicles: {},
  lotOwnership: "",
  houseOwnership: "",
  lotOwnershipOther: "",
  houseOwnershipOther: "",
  cultivatedLand: [],
  agriculturalProducts: [],
  agriculturalMachineries: [],
  animalsRaised: [],
  petAnimals: [],
  fisheriesEntries: [],
  boatEntries: [],
  nipaEntries: [],
  fishingGearEntries: [],
  environmentSanitation: {
    selectedWaterSource: [],
    selectedWaterLocation: "",
    isWaterAvailable: "",
    selectedWaterSource2: [],
    selectedWaterLocation2: "",
    isWaterAvailable2: "",
    selectedToiletFacility: [],
    selectedToiletLocation: "",
    toiletSharing: "",
    selectedGarbageDisposal: [],
  },
  familyMembers: [],
  familyDemographics: [{ // Should be populated by fetched data, especially for the head
    firstName: "",
    middleName: "",
    lastName: "",
    relationship: "HH Head",
    status: "Present Member",
    sex: "",
    gender: "",
    birthdate: "",
    withSeniorId: "",
    seniorCitizenIdNo: "",
    birthPlace: "",
    citizenship: "Philippines",
    migrationStatus: "",
    registeredVoter: "",
    civilStatus: "",
    soloParentId: "",
    religion: "",
    skills: "",
    organizations: "",
    ethnicity: "",
    schoolingStatus: "",
    schoolingReasons: [],
    schoolName: "",
    educationalAttainment: [],
    degree: ""
  }],
  lastPageData: {
    foodSecurity: {},
    socialProtection: { I1: [], I2: [] },
    peaceAndOrder: { J1: "", J2: "", J3: "", J4: "" }
  }
};

// Helper: Convert camelCase keys to snake_case for backend compatibility
function camelToSnake(str) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function normalizeMemberFields(member) {
  if (!member || typeof member !== 'object') return {};
  const result = {};
  Object.keys(member).forEach(key => {
    // Only convert if not already snake_case
    if (key.match(/[A-Z]/)) {
      result[camelToSnake(key)] = member[key];
    } else {
      result[key] = member[key];
    }
  });
  return result;
}

// Add these mappings before the fetchHouseholdData function (inside the component)
const houseStructureOptions = [
  { value: 1, label: "1 - Duplex" },
  { value: 2, label: "2 - Commercial/Industrial/Agricultural" },
  { value: 3, label: "3 - Extension" },
  { value: 4, label: "4 - Institutional Living Quarters" },
  { value: 5, label: "5 - Multi-unit Residence" },
  { value: 6, label: "6 - Single House" },
  { value: 99, label: "99 - Others, specify" },
];
const externalHouseWallOptions = [
  { value: 1, label: "1 - Asbestos/Glass" },
  { value: 2, label: "2 - Bamboo/Sawali/Cogon/Nipa" },
  { value: 3, label: "3 - Concrete/Brick/Stone" },
  { value: 4, label: "4 - Galvanized Iron/Aluminum" },
  { value: 5, label: "5 - Half-Concrete, Brick, Stone/Half Wood" },
  { value: 6, label: "6 - Makeshift/Salvaged/Improvised" },
  { value: 7, label: "7 - Wood" },
  { value: 8, label: "8 - No Walls" },
  { value: 9, label: "9 - Container Van" },
  { value: 99, label: "99 - Others, specify" },
];
const roofingMatOptions = [
  { value: 1, label: "1 - Bamboo/Sawali/Cogon/Nipa" },
  { value: 2, label: "2 - Galvanized Iron/Aluminum" },
  { value: 3, label: "3 - Makeshift/Salvaged/Improvised" },
  { value: 4, label: "4 - Mixed materials but more of cogon/nipa/anahaw" },
  { value: 5, label: "5 - Mixed materials but more of galvanized iron" },
  { value: 6, label: "6 - Cement" },
  { value: 7, label: "7 - Wood" },
  { value: 8, label: "8 - Mixed materials but more of makeshift/salvaged/improvised" },
  { value: 99, label: "99 - Others, specify" },
];
const floorMatOptions = [
  { value: 1, label: "1 - Bamboo" },
  { value: 2, label: "2 - Cement" },
  { value: 3, label: "3 - Cemented with linoleum" },
  { value: 4, label: "4 - Cemented with tiles" },
  { value: 5, label: "5 - Wood" },
  { value: 6, label: "6 - Soil" },
  { value: 7, label: "7 - Soil with linoleum" },
  { value: 99, label: "99 - Others, specify" },
];
const sourceElectOptions = [
  { value: 1, label: "1 - Electricity" },
  { value: 2, label: "2 - Kerosene (Gaas)" },
  { value: 3, label: "3 - LPG" },
  { value: 4, label: "4 - Battery" },
  { value: 5, label: "5 - Biogas" },
  { value: 6, label: "6 - Solar Energy" },
  { value: 7, label: "7 - Barangay Generator" },
  { value: 8, label: "8 - No Electricity" },
  { value: 99, label: "99 - Others, Specify" },
];
const cookingFacOptions = [
  { value: 1, label: "1 - Electricity" },
  { value: 2, label: "2 - Charcoal" },
  { value: 3, label: "3 - Kerosene (Gaas)" },
  { value: 4, label: "4 - LPG" },
  { value: 5, label: "5 - Wood" },
  { value: 6, label: "6 - Biogas" },
  { value: 99, label: "99 - Others, specify" },
];
const houseLocationOptions = [
  { value: 1, label: "1 - Along estero/creek/waterways" },
  { value: 2, label: "2 - Along river banks/shoreline" },
  { value: 3, label: "3 - Along railroad" },
  { value: 4, label: "4 - In the garbage dumpsite" },
  { value: 5, label: "5 - Under the bridge" },
  { value: 6, label: "6 - In danger areas such as cliff, hillside, or mountain slopes" },
  { value: 7, label: "7 - In Temporary or permanent relocation area" },
  { value: 8, label: "8 - In a structure without roof/wall or in a housing unit with light materials in its roof and/or walls" },
  { value: 9, label: "9 - In government land without legally recognizable claims to the land" },
  { value: 10, label: "10 - In private land which they do not own" },
  { value: 99, label: "99 - Others, specify" },
];
const ownershipOptions = [
  { value: 1, label: "1 - Pagmamay-ari" },
  { value: 2, label: "2 - Nirentahan" },
  { value: 3, label: "3 - Hinuhulugan" },
  { value: 4, label: "4 - Nagagamit ng libre nang walang pahintulot" },
  { value: 5, label: "5 - Caretaker" },
  { value: 6, label: "6 - Pagmamay-ari ng Pamilya" },
  { value: 7, label: "7 - Pagmamay-ari ng Gobyerno" },
  { value: 8, label: "8 - PNR Lot" },
  { value: 99, label: "99 - Others, specify" },
];
const shapeOptions = [
  { value: 1, label: "1 - Regular" },
  { value: 2, label: "2 - Irregular" },
  { value: 3, label: "3 - Di Alam" },
];
const houseExtensionOptions = [
    { value: 1, label: "1 - Oo, pero pinangasiwaan ng Engineer/Arkitekto" },
    { value: 2, label: "2 - Oo, pero hindi pinangasiwaan ng Engineer/Arkitekto" },
    { value: 3, label: "3 - Hindi" },
    { value: 4, label: "4 - Hindi Alam" },
  ];
const wallThicknessOptions = [
  { value: 1, label: "1 - Oo" },
  { value: 2, label: "2 - Hindi" },
  { value: 3, label: "3 - Hindi Alam" },
];
const steelStandardOptions = [
  { value: 1, label: "1 - Oo, 10 mm na bakal ang ginamit, nakatali at naayon sa tamang pagitan" },
  { value: 2, label: "2 - Hindi, kakaunti at mas maliit sa 10mm na bakal" },
  { value: 3, label: "3 - Walang bakal na ginamit" },
  { value: 4, label: "4 - Hindi Alam" },
];
const wallSupportOptions = [
  { value: 1, label: "1 - Wala, lahat ng suportadong dingding ay mas makitid sa 3 metrong luwang" },
  { value: 2, label: "2 - Oo, isa lamang ang dingding na walang suporta o tukod may luwang na higit 3 metro" },
  { value: 3, label: "3 - Hindi Alam" },
];
const gableWallMaterialOptions = [
  { value: 1, label: "1 - Yari sa bakal" },
  { value: 2, label: "2 - Yari sa light materials, hollow blocks, at walang kabalyete" },
  { value: 3, label: "3 - Hindi maayos ang paglalagay ng hollow blocks, laryo (brick), o bato" },
  { value: 4, label: "4 - Hindi Alam" },
];
const foundationMaterialOptions = [
  { value: 1, label: "1 - Konkreto" },
  { value: 2, label: "2 - Hindi konkreto" },
  { value: 3, label: "3 - Hindi Alam" },
];
const soilConditionOptions = [
  { value: 1, label: "1 - Matigas" },
  { value: 2, label: "2 - Malambot" },
  { value: 3, label: "3 - Hindi Alam" },
];
const houseConditionOptions = [
  { value: 1, label: "1 - Matibay" },
  { value: 2, label: "2 - Mahina/Mahuna" },
  { value: 3, label: "3 - Hindi Alam" },
];
const disasterTypeOptions = [
  { value: 1, label: "1 - Bagyo" },
  { value: 2, label: "2 - Baha" },
  { value: 3, label: "3 - Lindol" },
  { value: 4, label: "4 - Tsunami" },
  { value: 5, label: "5 - Pagguho ng lupa" },
  { value: 6, label: "6 - Pagsabog ng bulkan" },
  { value: 7, label: "7 - Man-made calamity" },
  { value: 99, label: "99 - Others, specify" }
];
const evacuationAreaOptions = [
  { value: 1, label: "1 - Barangay/Rural Health Center" },
  { value: 2, label: "2 - Barangay Hall" },
  { value: 3, label: "3 - Barangay Evacuation Center" },
  { value: 4, label: "4 - City/Municipal Evacuation Center" },
  { value: 5, label: "5 - Barangay Multi-Purpose Center" },
  { value: 6, label: "6 - City/Municipal Multi-Purpose Center" },
  { value: 7, label: "7 - Public School" },
  { value: 8, label: "8 - Private School" },
  { value: 9, label: "9 - Church" },
  { value: 10, label: "10 - Covered Court" },
  { value: 11, label: "11 - Gymnasium" },
  { value: 12, label: "12 - Public Park" },
  { value: 13, label: "13 - Relatives" },
  { value: 14, label: "14 - Neighbor" },
  { value: 15, label: "15 - Hindi nag-evacuate" },
  { value: 99, label: "99 - Others, specify" }
];
const disasterImpactOptions = [
  { value: 1, label: "1 - Nasira ang bahay" },
  { value: 2, label: "2 - Nasira ang pananim" },
  { value: 3, label: "3 - Nasira ang kagamitan" },
  { value: 4, label: "4 - Nagtamo ng injury" },
  { value: 5, label: "5 - Walang epekto o damage" },
  { value: 6, label: "6 - Namatayan" },
  { value: 99, label: "99 - Others, specify" }
];
const assistanceTypeOptions = [
  { value: 1, label: "1 - Pagkain at tubig" },
  { value: 2, label: "2 - Damit at personal na gamit" },
  { value: 3, label: "3 - Materyales sa pagpapagawa ng bahay" },
  { value: 4, label: "4 - Pera" },
  { value: 5, label: "5 - Hygiene kit" },
  { value: 6, label: "6 - Psychosocial support" },
  { value: 99,label: "99 - Others, specify" }
];
const assistanceSourceOptions = [
  { value: 1, label: "1 - National Government" },
  { value: 2, label: "2 - Regional Government" },
  { value: 3, label: "3 - Provincial Government" },
  { value: 4, label: "4 - City/Municipal Government" },
  { value: 5, label: "5 - Barangay Government" },
  { value: 6, label: "6 - NGO" },
  { value: 7, label: "7 - Private Organization" },
  { value: 8, label: "8 - Private Person" },
  { value: 9, label: "9 - International Organization" },
  { value: 99, label: "99 - Others, specify" }
]

// Helper to map string value to option value
function getOptionValueByLabel(options, label) {
  if (!label) return null;
  // Try to match by label (full label), or by substring after dash, or by case-insensitive match
  const found = options.find(
    opt =>
      opt.label === label ||
      opt.label.split(" - ")[1]?.trim().toLowerCase() === label.trim().toLowerCase() ||
      opt.label.toLowerCase().includes(label.trim().toLowerCase())
  );
  return found ? found.value : null;
}

const DetailView = ({ cityList }) => {
  const { headId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const headerDataFromRoute = location.state?.rowData || {}; // Renamed to avoid conflict

  const [formData, setFormData] = useState(() => merge({}, initialDetailFormData)); // Initialize with a deep clone
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false); // For save operation
  const toastRef = useRef(null); // For notifications

  const showToast = useCallback((message) => {
    if (toastRef.current) {
      toastRef.current.textContent = message;
      toastRef.current.open = true;
    }
  }, []);

  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  // Add these mappings at the top of your component (outside useEffect)
  const applianceMapping = {
    "1": "Radio/Tape recorder/Stereo",
    "2": "Television (TV)",
    "3": "VCR/Karaoke/Videoke",
    "4": "Video Games",
    "5": "Airconditioner",
    "6": "Electric Fan",
    "7": "Exhaust Fan",
    "8": "Flat Iron",
    "9": "Refrigerator",
    "10": "Freezer",
    "11": "Water Heater/Electric Thermos/Airpot",
    "12": "Rice cooker",
    "13": "Oven Toaster",
    "14": "Microwave oven",
    "15": "Gas Range",
    "16": "Washing Machine",
    "17": "Dryer",
    "18": "Computer/Laptop",
    "19": "Printer",
    "99": "Others (hair dryer, rechargeable lamp, etc.)"
  };

  const ictDevicesMapping = {
    "1": "Radio/Tape recorder/Stereo",
    "2": "Television (TV)",
    "3": "VCR/Karaoke/Videoke",
    "4": "Video Games",
    "5": "Computer/Laptop",
    "6": "Printer",
    "7": "CD/VCD/DVD Player",
    "8": "Telephone",
    "9": "Mobile Phone",
    "10": "Tablet",
  };

  const vehicleMapping = {
    "1": "Three wheel (Kuliglig)",
    "2": "Jeepney",
    "3": "Car",
    "4": "Owner Type Jeep",
    "5": "Tricycle",
    "6": "Truck",
    "7": "Pick-up",
    "8": "Van",
    "9": "SUV",
    "10": "Bicycle",
    "11": "Motorcycle",
    "12": "Pedicab",
    "13": "Boat",
    "14": "Motorboat",
    "15": "Bus",
    "16": "Coaster",
    "17": "Pump boat",
    "18": "Yacht",
    "19": "Raft (balsa)",
    "20": "Multi-cab",
    "21": "Electric motorcycle",
    "22": "Electric jeepney",
    "23": "Electric car",
    "99": "Others"
  };
  const statusMapping = {
    "27": "A - Active",
    "36": "A - Active",
    "37": "A - Active",
    "39": "A - Active",
    "42": "A - Active",
    "45": "A - Active",
    "46": "A - Active",
    "47": "A - Active",
    "48": "A - Active",
    "49": "A - Active",
    "40": "D - Died",
    "41": "T - Transferred",
  }
  // Fetch detailed data for the household
  useEffect(() => {
    const fetchHouseholdData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/mass-upload/${headId}`, { // Updated Endpoint
          headers: { "x-api-key": process.env.REACT_APP_API_KEY },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to fetch data: ${response.status}`);
        }
        const rawData = await response.json();
        // console.log("rawData:", rawData)
        // Create a new object based on initialDetailFormData to ensure all keys exist
        let transformedData = merge({}, initialDetailFormData);
        
        // 1. Map headDetails
        if (rawData.headDetails) {
          const headDetails = rawData.headDetails;
          // Direct mappings from family_head table
          transformedData.householdType = headDetails.household_type || transformedData.householdType;
          if(headDetails.family_type_id === 3){
            transformedData.familyType = "Extended Family"
          }
          else{
            transformedData.familyType = "Main Family (Owner)"
          }
          //transformedData.familyType = headDetails.family_type_id || transformedData.familyType;
          transformedData.status = headDetails.status || transformedData.status;
          transformedData.numFamilyMembers = headDetails.num_family_members !== null ? String(headDetails.num_family_members) : transformedData.numFamilyMembers;
          transformedData.yearMoved = headDetails.year_moved || transformedData.yearMoved;
          transformedData.prevLocation = headDetails.previous_location || transformedData.prevLocation;
          transformedData.headID = headDetails.extended_head_id || headId;

          // Person details (p.* in backend)
          transformedData.firstName = headDetails.first_name || transformedData.firstName;
          transformedData.middleName = headDetails.middle_name || transformedData.middleName;
          transformedData.lastName = headDetails.last_name || transformedData.lastName;
          transformedData.suffix = headDetails.suffix_name || transformedData.suffix;
          
          // Address details (a.* in backend)
          // Fix: Use headDetails.latitude and headDetails.longitude directly
          transformedData.longitude = headDetails.longitude !== undefined && headDetails.longitude !== null
            ? String(headDetails.longitude)
            : transformedData.longitude;
          transformedData.latitude = headDetails.latitude !== undefined && headDetails.latitude !== null
            ? String(headDetails.latitude)
            : transformedData.latitude;
          transformedData.houseNo = headDetails.house_no || headDetails.house_no_street || transformedData.houseNo;
          transformedData.street = headDetails.street_name || headDetails.street || transformedData.street;
          transformedData.purokSitio = headDetails.purok_sitio || transformedData.purokSitio;
          transformedData.barangay = headDetails.address_barangay || headDetails.barangay || transformedData.barangay;
          transformedData.cityMunicipality = headDetails.address_city_municipality || headDetails.municipality || transformedData.cityMunicipality;
          transformedData.province = headDetails.address_province || headDetails.province || transformedData.province;
          transformedData.municipality = headDetails.address_city_municipality || headDetails.municipality || transformedData.municipality;

          // DRRM details
          transformedData.ecologicalSetting = headDetails.ecological_setting_id || transformedData.ecologicalSetting;
          transformedData.buildingPermit = Number(headDetails.building_permit) || transformedData.buildingPermit;
          transformedData.houseBuiltBefore1990 = Number(headDetails.house_built_before_1990) || transformedData.houseBuiltBefore1990;
          transformedData.houseAffectedByDisaster = Number(headDetails.house_affected_by_disaster) || transformedData.houseAffectedByDisaster;
          // Contact Info (mobileProvider and contactNumber as arrays or strings)
          if (Array.isArray(headDetails.mobileProvider)) {
            transformedData.mobileProvider = [
              headDetails.mobileProvider[0] || "",
              headDetails.mobileProvider[1] || "",
              headDetails.mobileProvider[2] || ""
            ];
          } else if (typeof headDetails.mobileProvider === "string") {
            const providersArr = headDetails.mobileProvider.split(',').map(p => p.trim());
            transformedData.mobileProvider = [
              providersArr[0] || "",
              providersArr[1] || "",
              providersArr[2] || ""
            ];
          }

          if (Array.isArray(headDetails.contactNumber)) {
            transformedData.contactNumber = [
              headDetails.contactNumber[0] || "",
              headDetails.contactNumber[1] || "",
              headDetails.contactNumber[2] || ""
            ];
          } else if (typeof headDetails.contactNumber === "string") {
            const contactNumbers = headDetails.contactNumber.split(',').map(cn => cn.trim());
            transformedData.contactNumber = [
              contactNumbers[0] || "",
              contactNumbers[1] || "",
              contactNumbers[2] || ""
            ];
          }
        }

        // --- NEW: If top-level mobileProvider/contactNumber exist as arrays, use them ---
        if (Array.isArray(rawData.mobileProvider)) {
          transformedData.mobileProvider = [
            rawData.mobileProvider[0] || "",
            rawData.mobileProvider[1] || "",
            rawData.mobileProvider[2] || ""
          ];
        }
        if (Array.isArray(rawData.contactNumber)) {
          transformedData.contactNumber = [
            rawData.contactNumber[0] || "",
            rawData.contactNumber[1] || "",
            rawData.contactNumber[2] || ""
          ];
        }
        // --- END NEW ---

        // 2. Map familyMembers to familyDemographics
        if (Array.isArray(rawData.familyMembers)) {
          transformedData.familyDemographics = rawData.familyMembers.map(member => {
            // Use backend keys directly, fallback to empty string if missing
            const demo = {
              firstName: member.first_name || "",
              middleName: member.middle_name || "",
              lastName: member.last_name || "",
              suffix: member.suffix || member.suffix_name || "",
              sex: member.sex || "",
              gender: member.gender || "",
              birthdate: formatDateForInput(member.birthdate || member.birth_date),
              birthPlace: member.birthplace || member.birth_place || "",
              citizenship: member.citizenship || "Philippines",
              civilStatus: member.civil_status || "",
              religion: member.religion || "",
              ethnicity: member.ethnicity || "",
              relationship: member.relationship || "",
              // Use status_id or fallback to "Present Member"
              status: member.status_id ? statusMapping[member.status_id] : "Present Member",
              withSeniorId: member.with_senior_id === 1 ? "1 Yes" : (member.with_senior_id === 0 ? "2 No" : ""),
              seniorCitizenIdNo: member.senior_citizen_id || "",
              migrationStatus: member.migration_status || "",
              registeredVoter: member.registered_voter && member.registered_voter.toLowerCase().includes("yes") ? "1 Yes" : (member.registered_voter && member.registered_voter.toLowerCase().includes("no") ? "2 No" : ""),
              soloParentId: member.solo_parent_id || "",
              skills: member.skills || member.skills_development_training || "",
              organizations: member.organizations || member.organization_membership || "",
              schoolingStatus: member.schooling_status === "1 In School" || member.schooling_status === "In School" ? "1 In School" : (member.schooling_status === "2 Out of School" || member.schooling_status === "Out of School" ? "2 Out of School" : ""),
              schoolingReasons: member.no_schooling_reason ? [member.no_schooling_reason] : [],
              schoolName: member.school_name || "",
              educationalAttainment: member.level_name ? [member.level_name] : [],
              degree: member.degree || member.degree_course || "",
              causeOfDeath: member.cause_death || member.cause_of_death || "",
            };
            // ...existing code for healthData...
            const healthData = {
                G17: member.G17 || defaultHealthDataForMerge.G17,
                G18: member.G18 ? { ...defaultHealthDataForMerge.G18, ...member.G18, date: formatDateForInput(member.G18.date) } : defaultHealthDataForMerge.G18,
                G19: member.G19 ? { ...defaultHealthDataForMerge.G19, ...member.G19, VitaminA: {...defaultHealthDataForMerge.G19.VitaminA, ...member.G19.VitaminA, date: formatDateForInput(member.G19.VitaminA?.date)}, Iron: {...defaultHealthDataForMerge.G19.Iron, ...member.G19.Iron, date: formatDateForInput(member.G19.Iron?.date)} } : defaultHealthDataForMerge.G19,
                G20: member.G20 ? { ...defaultHealthDataForMerge.G20, ...member.G20, DateLastBreastfed: formatDateForInput(member.G20.DateLastBreastfed) } : defaultHealthDataForMerge.G20,
                G21: member.G21 || defaultHealthDataForMerge.G21,
            };
            return { ...initialDetailFormData.familyDemographics[0], ...demo, ...healthData };
          });
          // Also populate familyMembers for compatibility
          transformedData.familyMembers = rawData.familyMembers.map(member => ({ ...member }));
        } else {
          // Defensive: always set as array
          transformedData.familyDemographics = [];
          transformedData.familyMembers = [];
        }
        // copy familyDemographics.status to familyMembers.status_id
        if (transformedData.familyDemographics && transformedData.familyMembers) {
          transformedData.familyMembers.forEach((member, index) => {
            // Check if the corresponding item exists in familyDemographics
            if (transformedData.familyDemographics[index]) {
              // Assign the status from familyDemographics to the current family member
              member.status_id = transformedData.familyDemographics[index].status;
            }
            if (transformedData.familyDemographics[index]) {
              // Assign the status from familyDemographics to the current family member
              member.schoolingReasons = transformedData.familyDemographics[index].schoolingReasons;
            }
          });
        }

        // 3. Map characteristics to householdAppliances, ictDevices, vehicles, lot/houseOwnership
        transformedData.householdAppliances = {};
        transformedData.vehicles = {};
        transformedData.ictDevices = {};

        // New: Extract from householdAssets object if present
        if (rawData.householdAssets && typeof rawData.householdAssets === 'object' && !Array.isArray(rawData.householdAssets)) {
          if (rawData.householdAssets.householdAppliances) {
            transformedData.householdAppliances = { ...rawData.householdAssets.householdAppliances };
          }
          if (rawData.householdAssets.ictDevices) {
            transformedData.ictDevices = { ...rawData.householdAssets.ictDevices };
          }
          if (rawData.householdAssets.vehicles) {
            transformedData.vehicles = { ...rawData.householdAssets.vehicles };
          }
        }

        if (rawData.householdAssets && Array.isArray(rawData.householdAssets)) {
          rawData.householdAssets.forEach(asset => {
            const assetType = asset.asset_type;
            const assetName = asset.asset_name;
            const quantity = asset.quantity || 0;
            if (assetType === "Appliance") {
              // Find the matching item in UIModel by label
              const found = (UIModel.socioeconomicForm.applianceItems || []).find(i => i.label === assetName);
              if (found) {
                transformedData.householdAppliances[String(found.number)] = quantity;
              }
            }
            if (assetType === "ICT") {
              const found = (UIModel.socioeconomicForm.applianceItems || []).find(i => i.label === assetName);
              if (found) {
                transformedData.ictDevices[String(found.number)] = quantity;
              }
            }
            if (assetType === "Vehicle") {
              const found = (UIModel.socioeconomicForm.vehicleItems || []).find(i => i.label === assetName);
              if (found) {
                transformedData.vehicles[String(found.number)] = quantity;
              }
            }
          });
        }

        if (rawData.characteristics && Array.isArray(rawData.characteristics)) {
          rawData.characteristics.forEach(char => {
            const value = char.quantity || char.value || 0;
            if (char.characteristic_name === "Lot Ownership") {
              transformedData.lotOwnership = value;
              transformedData.lotOwnershipOther = char.other_value || "";
            }
            if (char.characteristic_name === "House Ownership") {
              transformedData.houseOwnership = value;
              transformedData.houseOwnershipOther = char.other_value || "";
            }
          });
        }

        // New API: characteristics is an object with string values
        if (rawData.characteristics && typeof rawData.characteristics === "object" && !Array.isArray(rawData.characteristics)) {
          const chars = rawData.characteristics;
          // Lot Ownership
          if (chars.LOT_OWNERSHIP) {
            if (typeof chars.LOT_OWNERSHIP === "object") {
              const value = chars.LOT_OWNERSHIP.value;
              const cVal = getOptionValueByLabel(ownershipOptions, value);
              transformedData.lotOwnership = cVal !== null ? cVal : "";
              transformedData.lotOwnershipOther = chars.LOT_OWNERSHIP.other || "";
            }
            else if (typeof chars.LOT_OWNERSHIP === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(ownershipOptions, chars.LOT_OWNERSHIP);
              transformedData.lotOwnership = cVal !== null ? cVal : "";
              transformedData.lotOwnershipOther = "";
            }
          }
          // House Ownership
          if (chars.HOUSE_OWNERSHIP) {
            if (typeof chars.HOUSE_OWNERSHIP === "object") {
              const value = chars.HOUSE_OWNERSHIP.value;
              const cVal = getOptionValueByLabel(ownershipOptions, value);
              transformedData.houseOwnership = cVal !== null ? cVal : "";
              transformedData.houseOwnershipOther = chars.HOUSE_OWNERSHIP.other || "";
            }
            else if (typeof chars.HOUSE_OWNERSHIP === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(ownershipOptions, chars.HOUSE_OWNERSHIP);
              transformedData.houseOwnership = cVal !== null ? cVal : "";
              transformedData.houseOwnershipOther = "";
            }
          }

          // House Structure
          if (chars.HOUSE_STRUCTURE) {
            if (typeof chars.HOUSE_STRUCTURE === "object") {
              const value = chars.HOUSE_STRUCTURE.value;
              const cVal = getOptionValueByLabel(houseStructureOptions, value);
              transformedData.houseStructure = cVal !== null ? cVal : "";
              transformedData.houseStructureOther = chars.HOUSE_STRUCTURE.other || "";
            }
            else if (typeof chars.HOUSE_STRUCTURE === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(houseStructureOptions, chars.HOUSE_STRUCTURE);
              transformedData.houseStructure = cVal !== null ? cVal : "";
              transformedData.houseStructureOther = "";
            }
          }

          // External Wall
          if (chars.EXTERNAL_WALL) {
            if( typeof chars.EXTERNAL_WALL === "object") {
              const value = chars.EXTERNAL_WALL.value;
              const cVal = getOptionValueByLabel(externalHouseWallOptions, value);
              transformedData.externalHouseWall = cVal !== null ? cVal : "";
              transformedData.externalHouseWallOther = chars.EXTERNAL_WALL.other || "";
            }
            else if (typeof chars.EXTERNAL_WALL === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(externalHouseWallOptions, chars.EXTERNAL_WALL);
              transformedData.externalHouseWall = cVal !== null ? cVal : "";
              transformedData.externalHouseWallOther = "";
            }
          }

          // Roofing
          if (chars.ROOFING) {
            if (typeof chars.ROOFING === "object") {
              const value = chars.ROOFING.value;
              const cVal = getOptionValueByLabel(roofingMatOptions, value);
              transformedData.roofingMat = cVal !== null ? cVal : "";
              transformedData.roofingMatOther = chars.ROOFING.other || "";
            }
            else if (typeof chars.ROOFING === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(roofingMatOptions, chars.ROOFING);
              transformedData.roofingMat = cVal !== null ? cVal : "";
              transformedData.roofingMatOther = "";
            }
          }

          // Flooring
          if (chars.FLOORING) {
            if (typeof chars.FLOORING === "object") {
              const value = chars.FLOORING.value;
              const cVal = getOptionValueByLabel(floorMatOptions, value);
              transformedData.floorMat = cVal !== null ? cVal : "";
              transformedData.floorMatOther = chars.FLOORING.other || "";
            }
            else if (typeof chars.FLOORING === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(floorMatOptions, chars.FLOORING);
              transformedData.floorMat = cVal !== null ? cVal : "";
              transformedData.floorMatOther = "";
            } 
          }

          // Source of Electricity
          if (chars.SOURCE_ELECTRICITY) {
            if (typeof chars.SOURCE_ELECTRICITY === "object") {
              const value = chars.SOURCE_ELECTRICITY.value;
              const cVal = getOptionValueByLabel(sourceElectOptions, value);
              transformedData.sourceElect = cVal !== null ? cVal : "";
              transformedData.sourceElectOther = chars.SOURCE_ELECTRICITY.other || "";
            }
            else if (typeof chars.SOURCE_ELECTRICITY === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(sourceElectOptions, chars.SOURCE_ELECTRICITY);
              transformedData.sourceElect = cVal !== null ? cVal : "";
              transformedData.sourceElectOther = "";
            } 
          }

          // Cooking
          if (chars.COOKING) {
            if (typeof chars.COOKING === "object") {
              const value = chars.COOKING.value;
              const cVal = getOptionValueByLabel(cookingFacOptions, value);
              transformedData.cookingFac = cVal !== null ? cVal : "";
              transformedData.cookingFacOther = chars.COOKING.other || "";
            }
            else if (typeof chars.COOKING === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(cookingFacOptions, chars.COOKING);
              transformedData.cookingFac = cVal !== null ? cVal : "";
              transformedData.cookingFacOther = "";
            } 
          }         
          // House Location
          if (chars.LOCATION) {
            if (typeof chars.LOCATION === "object") {
              const value = chars.LOCATION.value;
              const cVal = getOptionValueByLabel(houseLocationOptions, value);
              transformedData.houseLocation = cVal !== null ? cVal : "";
              transformedData.houseLocationOther = chars.LOCATION.other || "";
            }
            else if (typeof chars.LOCATION === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(houseLocationOptions, chars.LOCATION);
              transformedData.houseLocation = cVal !== null ? cVal : "";
              transformedData.houseLocationOther = "";
            }
          }
          if (chars.SHAPE) {
            const cVal = getOptionValueByLabel(shapeOptions, chars.SHAPE);
            transformedData.houseShape = cVal !== null ? cVal : "";
          }
          if (chars.EXTENSION) {
            const cVal = getOptionValueByLabel(houseExtensionOptions, chars.EXTENSION);
            transformedData.houseExtension = cVal !== null ? cVal : "";
          }
          if (chars.THICKNESS) {
            const cVal = getOptionValueByLabel(wallThicknessOptions, chars.THICKNESS);
            transformedData.wallThickness = cVal !== null ? cVal : "";
          }
          if (chars.STANDARD) {
            const cVal = getOptionValueByLabel(steelStandardOptions, chars.STANDARD);
            transformedData.steelStandard = cVal !== null ? cVal : "";
          }
          if (chars.SUPPORT) {
            const cVal = getOptionValueByLabel(wallSupportOptions, chars.SUPPORT);
            transformedData.wallSupport = cVal !== null ? cVal : "";
          }
          if (chars.MATERIAL) {
            const cVal = getOptionValueByLabel(gableWallMaterialOptions, chars.MATERIAL);
            transformedData.gableWallMaterial = cVal !== null ? cVal : "";
          }
          if (chars.FOUNDATION) {
            const cVal = getOptionValueByLabel(foundationMaterialOptions, chars.FOUNDATION);
            transformedData.foundationMaterial = cVal !== null ? cVal : "";
          }
          if (chars.SOIL) {
            const cVal = getOptionValueByLabel(soilConditionOptions, chars.SOIL);
            transformedData.soilCondition = cVal !== null ? cVal : "";
          }
          if (chars.CONDITION) {
            const cVal = getOptionValueByLabel(houseConditionOptions, chars.CONDITION);
            transformedData.houseCondition = cVal !== null ? cVal : "";
          }
          if (chars.AS) {
            if (typeof chars.AS === "object") {
              const value = chars.AS.value;
              const cVal = getOptionValueByLabel(assistanceSourceOptions, value);
              transformedData.assistanceSource = cVal !== null ? cVal : "";
              transformedData.assistanceSourceOther = chars.AS.other || "";
            }
            else if (typeof chars.AS === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(assistanceSourceOptions, chars.AS);
              transformedData.assistanceSource = cVal !== null ? cVal : "";
              transformedData.assistanceSourceOther = "";
            }
          }
          if (chars.AT) {
            if (typeof chars.AT === "object") {
              const value = chars.AT.value;
              const cVal = getOptionValueByLabel(assistanceTypeOptions, value);
              transformedData.assistanceType = cVal !== null ? cVal : "";
              transformedData.assistanceTypeOther = chars.AS.other || "";
            }
            else if (typeof chars.AT === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(assistanceTypeOptions, chars.AT);
              transformedData.assistanceType = cVal !== null ? cVal : "";
            }  
          }
          if (chars.DI) {
            if (typeof chars.DI === "object") {
              const value = chars.DI.value;
              const cVal = getOptionValueByLabel(disasterImpactOptions, value);
              transformedData.disasterImpact = cVal !== null ? cVal : "";
              transformedData.disasterImpactOther = chars.DI.other || "";
            }
            else if (typeof chars.DI === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(disasterImpactOptions, chars.DI);
              transformedData.disasterImpact = cVal !== null ? cVal : "";
              transformedData.disasterImpactOther = "";
            }
          }
          if (chars.DT) {
            if (typeof chars.DT === "object") {
              const value = chars.DT.value;
              const cVal = getOptionValueByLabel(disasterTypeOptions, value);
              transformedData.disasterType = cVal !== null ? cVal : "";
              transformedData.disasterTypeOther = chars.DT.other || "";
            }
            else if (typeof chars.DT === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(disasterTypeOptions, chars.DT);
              transformedData.disasterType = cVal !== null ? cVal : "";
              transformedData.disasterTypeOther = "";
            }
          }
          if (chars.EA) {
            if (typeof chars.EA === "object") {
              const value = chars.EA.value;
              const cVal = getOptionValueByLabel(evacuationAreaOptions, value);
              transformedData.evacuationArea = cVal !== null ? cVal : "";
              transformedData.evacuationAreaOther = chars.EA.other || "";
            }
            else if (typeof chars.EA === "string") {
              // If it's a string, we assume it's a label and try to find the value
              const cVal = getOptionValueByLabel(evacuationAreaOptions, chars.EA);
              transformedData.evacuationArea = cVal !== null ? cVal : "";
              transformedData.evacuationAreaOther = "";
            }
          }
        }

        // 4. Map internetProviders
        if (rawData.internetProviders && Array.isArray(rawData.internetProviders)) {
          const primaryProvider = rawData.internetProviders.find(ip => ip.is_primary);
          if (primaryProvider) {
            transformedData.internetProvider = primaryProvider.name;
            if (primaryProvider.name === "Others" || primaryProvider.name === "Other") {
              transformedData.internetProviderOther = primaryProvider.type || "";
            }
          } else if (rawData.internetProviders.length > 0) {
            transformedData.internetProvider = rawData.internetProviders[0].name;
             if (rawData.internetProviders[0].name === "Others" || rawData.internetProviders[0].name === "Other") {
              transformedData.internetProviderOther = rawData.internetProviders[0].type || "";
            }
          }
        }
        
        // 5. Map environmentSanitation
        if (rawData.environmentSanitation) {
          const es = rawData.environmentSanitation;
          // Filter for all water sources used for "General"
          const generalWaterSources = es.waterSources?.filter(ws => ws.used_for === 'General') || [];
          // Filter for all water sources used for "Drinking/Cooking"
          const drinkingCookingWaterSources = es.waterSources?.filter(ws => ws.used_for === 'Drinking/Cooking') || [];

          // Assign values based on the "General" sources
          transformedData.environmentSanitation.selectedWaterSource = generalWaterSources.map(ws => ws.source_type);
          transformedData.environmentSanitation.selectedWaterLocation = generalWaterSources[0]?.location_description || "";
          transformedData.environmentSanitation.isWaterAvailable = generalWaterSources[0]?.is_available_year_round ? "Yes" : "No";

          // Assign values based on the "Drinking/Cooking" sources
          transformedData.environmentSanitation.selectedWaterSource2 = drinkingCookingWaterSources.map(ws => ws.source_type);
          transformedData.environmentSanitation.selectedWaterLocation2 = drinkingCookingWaterSources[0]?.location_description || "";
          transformedData.environmentSanitation.isWaterAvailable2 = drinkingCookingWaterSources[0]?.is_available_year_round ? "Yes" : "No";
          
          transformedData.environmentSanitation.selectedToiletFacility = es.toiletFacilities?.map(tf => tf.type || tf.facility_type) || [];
          transformedData.environmentSanitation.selectedToiletLocation = es.toiletFacilities?.[0]?.location_description || "";
          transformedData.environmentSanitation.toiletSharing = es.toiletFacilities?.[0]?.shared_with || "";

          transformedData.environmentSanitation.selectedGarbageDisposal = es.garbageDisposal?.map(gd => gd.method || gd.disposal_method) || [];
        }

        // 6. Map agriculture data
        if (rawData.agriculture) {
          transformedData.cultivatedLand = rawData.agriculture.cultivatedLand || [];
          transformedData.agriculturalProducts = rawData.agriculture.agriculturalProducts || [];
          transformedData.agriculturalMachineries = rawData.agriculture.agriculturalMachineries || [];
          transformedData.animalsRaised = rawData.agriculture.animalsRaised || [];
          transformedData.petAnimals = rawData.agriculture.petAnimals || [];
        }

        // 7. Map fisheries data
        if (rawData.fisheries) {
          transformedData.fisheriesEntries = rawData.fisheries.fisheriesInfo || [];
          transformedData.boatEntries = rawData.fisheries.boatsInfo || rawData.boatsRows || [];
          transformedData.nipaEntries = rawData.fisheries.nipaProduction || [];
          //transformedData.fishingGearEntries = rawData.fisheries.fishingGear || [];
          if (rawData.fisheries && rawData.fisheries.fishingGear) {
            const transformedFishingGear = rawData.fisheries.fishingGear.reduce((acc, entry) => {
              // Check for both 'category' and 'gear' from the API response
              if (entry.category && entry.gear) {
                if (!acc[entry.category]) {
                  acc[entry.category] = {};
                }
                // Use the 'gear' key from the API response
                acc[entry.category][entry.gear] = entry.quantity || 0;
              }
              return acc;
            }, {});
            
            // Update the formData with the transformed object
            transformedData.fishingGearEntries = transformedFishingGear;
          } else {
            // Handle cases where the data might be null or not an array
            transformedData.fishingGearEntries = {};
          }
        }

        // 8. Map lastPageData (foodSecurity, socialProtection, peaceAndOrder)
        if (rawData.foodSecurity && Array.isArray(rawData.foodSecurity)) {
          transformedData.lastPageData.foodSecurity = rawData.foodSecurity.reduce((obj, item) => {
            obj[item.question_code] = item.response;
            return obj;
          }, {});
        }
        if (rawData.socialProtection && Array.isArray(rawData.socialProtection)) {
          transformedData.lastPageData.socialProtection = { I1: [], I2: [] };
          rawData.socialProtection.forEach(sp => {
            if (sp.service_code && sp.service_code.startsWith("I1")) transformedData.lastPageData.socialProtection.I1.push(sp.service_name);
            if (sp.service_code && sp.service_code.startsWith("I2")) transformedData.lastPageData.socialProtection.I2.push(sp.service_name);
          });
        }
        if (rawData.peaceAndOrder) {
          transformedData.lastPageData.peaceAndOrder = {
            J1: rawData.peaceAndOrder.j1_peace_community_status || "",
            J2: rawData.peaceAndOrder.j2_specific_threats || "",
            J3: rawData.peaceAndOrder.j3_area_location || "",
            J4: rawData.peaceAndOrder.j4_remarks || "",
          };
        }
        // console.log("Transformed Data:", transformedData);
        setFormData(transformedData);
        showToast("Data loaded for editing.");
      } catch (error) {
        console.error("Error fetching or processing household data:", error);
        showToast(`Error loading data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (headId) {
      fetchHouseholdData();
    }
  }, [headId, formatDateForInput]);

  const handleFormChange = useCallback((field, value, index = null) => {
    setFormData(prev => {
      if (field === "familyMembers" && typeof value === 'object' && value !== null) {
        if(value[0] !== undefined){
          return { ...prev, [field]: value };
        }
        else{
          return { ...prev, ...value };
        }
      }
      if (field === "province" || field === "municipality" || field === "barangay" || field === "cityMunicipality") {
        if (field === "province") return { ...prev, province: value, municipality: "", barangay: "", cityMunicipality: "" };
        if (field === "municipality") return { ...prev, municipality: value, barangay: "" };
        if (field === "cityMunicipality") return { ...prev, cityMunicipality: value, barangay: "" };
        return { ...prev, [field]: value };
      }
      if (field === "all") return value;
      if (index !== null) {
        if (field === "mobileProvider" || field === "contactNumber") {
          const currentArray = Array.isArray(prev[field]) ? prev[field] : ["", "", ""];
          const newArray = [...currentArray];
          newArray[index] = value;
          return { ...prev, [field]: newArray };
        }
        if (Array.isArray(prev[field])) {
            return { ...prev, [field]: prev[field].map((item, i) => (i === index ? value : item)) };
        }
        console.warn(`Indexed update attempted on non-array field: ${field}`);
        return prev;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const validateManualForm = () => {
    const requiredFields = ["province", "cityMunicipality", "barangay", "firstName", "lastName"];
    for (const field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        alert(`Please fill in the required field: ${field.replace(/([A-Z])/g, " $1")}`);
        return false;
      }
    }
    return true;
  };

  const handleNext = useCallback(() => {
    if (currentStep === 1 && !validateManualForm()) {
      return;
    }
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, formData, validateManualForm]);

  const handlePrevious = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const formatDate = useCallback((input) => {
    if (!input) return null;
    const d = new Date(input);
    if (isNaN(d)) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!UIModel || typeof UIModel.payload !== 'object' || UIModel.payload === null) {
      console.error("Error: UIModel.payload is not a valid object.", UIModel);
      alert("Critical error: Payload template is missing or invalid. Cannot save data.");
      return;
    }

    setIsBusy(true);
    try {
      //console.log('formData:', formData)
      const headDemographics = formData.familyDemographics && formData.familyDemographics.length > 0
        ? formData.familyDemographics[0]
        : {};

      // NEW: Build senderId from userRole + userId stored in localStorage
      const storedUserRole = localStorage.getItem("userRole");
      const storedUserId = localStorage.getItem("userId");

      const payloadTemplate = JSON.parse(JSON.stringify(UIModel.payload));
      const payload = {
        ...payloadTemplate,
        // NEW FIELD
        senderId: parseInt(storedUserId, 10) || 1,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        suffix: formData.suffix,
        birthdate: formatDate(headDemographics.birthdate),
        birthplace: headDemographics.birthPlace,
        citizenship: headDemographics.citizenship,
        sex: headDemographics.sex || null,
        gender: headDemographics.gender,
        civilStatus: headDemographics.civilStatus,
        houseNo: formData.houseNo,
        street: formData.street,
        purokSitio: formData.purokSitio,
        barangay: formData.barangay,
        cityMunicipality: formData.cityMunicipality,
        province: formData.province,
        latitude: parseFloat(formData.latitude) || null,
        longitude: parseFloat(formData.longitude) || null,
        mobileProvider: formData.mobileProvider,
        contactNumber: formData.contactNumber,
        internetProvider: formData.internetProvider,
        internetProviderOther: formData.internetProviderOther,
        householdAppliances: formData.householdAppliances,
        ictDevices: formData.ictDevices,
        vehicles: formData.vehicles,
        lotOwnership: formData.lotOwnership?.value ?? formData.lotOwnership ?? "",
        lotOwnershipType: formData.lotOwnership?.type ?? "",
        houseOwnership: formData.houseOwnership?.value ?? formData.houseOwnership ?? "",
        houseOwnershipType: formData.houseOwnership?.type ?? "",
        lotOwnershipOther: formData.lotOwnershipOther,
        houseOwnershipOther: formData.houseOwnershipOther,
        houseStructure: formData.houseStructure?.value ?? formData.houseStructure ?? "",
        houseStructureType: formData.houseStructure?.type ?? "",
        houseStructureOther: formData.houseStructureOther,
        externalHouseWall: formData.externalHouseWall?.value ?? formData.externalHouseWall ?? "",
        externalHouseWallType: formData.externalHouseWall?.type ?? "",
        externalHouseWallOther: formData.externalHouseWallOther,
        roofingMat: formData.roofingMat?.value ?? formData.roofingMat ?? "",
        roofingMatType: formData.roofingMat?.type ?? "",
        roofingMatOther: formData.roofingMatOther,
        floorMat: formData.floorMat?.value ?? formData.floorMat ?? "",
        floorMatType: formData.floorMat?.type ?? "",
        floorMatOther: formData.floorMatOther,
        sourceElect: formData.sourceElect?.value ?? formData.sourceElect ?? "",
        sourceElectType: formData.sourceElect?.type ?? "",
        sourceElectOther: formData.sourceElectOther,
        cookingFac: formData.cookingFac?.value ?? formData.cookingFac ?? "",
        cookingFacType: formData.cookingFac?.type ?? "",
        cookingFacOther: formData.cookingFacOther,
        cultivatedLand: formData.cultivatedLand,
        agriculturalProducts: formData.agriculturalProducts,
        agriculturalMachineries: formData.agriculturalMachineries,
        animalsRaised: formData.animalsRaised,
        petAnimals: formData.petAnimals,
        fisheriesEntries: formData.fisheriesEntries,
        boatEntries: formData.boatEntries,
        nipaEntries: formData.nipaEntries,
        fishingGearEntries: formData.fishingGearEntries,
        environmentSanitation: formData.environmentSanitation,
        houseExtension: formData.houseExtension,
        houseExtensionOther: formData.houseExtensionOther,
        wallThickness: formData.wallThickness,
        wallThicknessOther: formData.wallThicknessOther,
        steelStandard: formData.steelStandard,
        steelStandardOther: formData.steelStandardOther,
        wallSupport: formData.wallSupport,
        wallSupportOther: formData.wallSupportOther,
        gableWallMaterial: formData.gableWallMaterial,
        gableWallMaterialOther: formData.gableWallMaterialOther,
        foundationMaterial: formData.foundationMaterial,
        foundationMaterialOther: formData.foundationMaterialOther,
        soilCondition: formData.soilCondition,
        soilConditionOther: formData.soilConditionOther,
        houseCondition: formData.houseCondition,
        houseConditionOther: formData.houseConditionOther,
        houseLocation: formData.houseLocation?.value ?? formData.houseLocation ?? "",
        houseLocationOther: formData.houseLocationOther,
        ecologicalSetting: formData.ecologicalSetting?.value ?? formData.ecologicalSetting ?? "",
        buildingPermit: formData.buildingPermit?.value ?? formData.buildingPermit ?? "",
        houseBuiltBefore1990: formData.houseBuiltBefore1990?.value ?? formData.houseBuiltBefore1990 ?? "",
        houseAffectedByDisaster: formData.houseAffectedByDisaster?.value ?? formData.houseAffectedByDisaster ?? "",
        houseShape: formData.houseShape?.value ?? formData.houseShape ?? "",
        disasterType: formData.disasterType?.value ?? formData.disasterType ?? "",
        disasterTypeOther: formData.disasterTypeOther?.value ?? formData.disasterTypeOther ?? "",
        evacuationArea: formData.evacuationArea?.value ?? formData.evacuationArea ?? "",
        evacuationAreaOther: formData.evacuationAreaOther?.value ?? formData.evacuationAreaOther ?? "",
        disasterImpact: formData.disasterImpact?.value ?? formData.disasterImpact ?? "",
        disasterImpactOther: formData.disasterImpactOther?.value ?? formData.disasterImpactOther ?? "",
        assistanceType: formData.assistanceType?.value ?? formData.assistanceType ?? "",
        assistanceTypeOther: formData.assistanceTypeOther?.value ?? formData.assistanceTypeOther ?? "",
        assistanceSource: formData.assistanceSource?.value ?? formData.assistanceSource ?? "",
        assistanceSourceOther: formData.assistanceSourceOther?.value ?? formData.assistanceSourceOther ?? "",
        familyMembers: (formData.familyMembers || []).map((member) => {
          // Normalize keys to camelCase for backend compatibility
          const normalized = normalizeMemberFields(member);
          const isOfwValue = normalized.isOFW === "1 - Yes" ? 1 : (normalized.isOFW === "2 - No" ? 0 : null);
          const seekingWorkValue = member.seekingWork === "1 - Actively seeking" ? 1 : (normalized.seekingWork === "2 - Not actively seeking" ? 0 : null);
          const withSeniorIdValue = normalized.withSeniorId === "1 Yes" || normalized.withSeniorId === 1 ? 1 : (normalized.withSeniorId === "2 No" || normalized.withSeniorId === 0 ? 0 : null);

          const g17Payload = normalized.G17 && typeof normalized.G17 === 'object' ? normalized.G17 : defaultHealthDataForMerge.G17;
          const g18Payload = normalized.G18 && typeof normalized.G18 === 'object' ? { ...normalized.G18, date: formatDate(normalized.G18.date) } : defaultHealthDataForMerge.G18;
          const g19Payload = normalized.G19 && typeof normalized.G19 === 'object' ? { ...normalized.G19, VitaminA: { ...normalized.G19.VitaminA, date: formatDate(normalized.G19.VitaminA?.date) }, Iron: { ...normalized.G19.Iron, date: formatDate(normalized.G19.Iron?.date) } } : defaultHealthDataForMerge.G19;
          const g20Payload = normalized.G20 && typeof normalized.G20 === 'object' ? { ...normalized.G20, DateLastBreastfed: formatDate(normalized.G20.DateLastBreastfed) } : defaultHealthDataForMerge.G20;
          const g21Payload = normalized.G21 || defaultHealthDataForMerge.G21;

          return {
            ...normalized,
            sex: normalized.sex || null,
            birthdate: formatDate(normalized.birthdate),
            is_ofw: isOfwValue,
            seeking_work: seekingWorkValue,
            with_senior_id: withSeniorIdValue,
            senior_citizen_id: normalized.seniorCitizenIdNo || normalized.senior_citizen_id || null,
            G17: g17Payload, G18: g18Payload, G19: g19Payload, G20: g20Payload, G21: g21Payload,
            withSeniorId: undefined, seniorCitizenIdNo: undefined,
          };
        }),
        lastPageData: formData.lastPageData,
        householdType: formData.householdType,
        familyType: formData.familyType,
        status: formData.status,
        numFamilyMembers: parseInt(formData.numFamilyMembers) || null,
        yearMoved: formData.yearMoved,
        prevLocation: formData.prevLocation,
      };

      payload.familyMembers = payload.familyMembers.map(member => {
        Object.keys(member).forEach(key => member[key] === undefined && delete member[key]);
        return member;
      });

      console.log("Payload for update:", payload);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/family-head/${headId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) {
        console.error("Server error response:", result);
        throw new Error(result.message || `Failed to update data. Status: ${response.status}`);
      }

      showToast("Update successful!");
    } catch (error) {
      console.error("Error updating data:", error);
      showToast(`Update failed: ${error.message}.`);
    } finally {
      setIsBusy(false);
    }
  }, [formData, headId, formatDate, navigate]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Do you really want to delete this person and its details?")) {
      return;
    }
    setIsBusy(true);
    try {
      // delete many from id 
      // const toDelete = [7849,7848,7771,7768,7761,7757,7756,7752,7750,7748,7747,7746,7743,7742,7739,7691,7690,7687,7686,7684,7680,7677,7675,7672,7669,7667,7665,7664,7661,7659,7656,7654,7651,7650,7649,7644,7641,7638,7635,7633,7631,6752,6751,6549,6547,6545,6544,6543,6542,6540,6538,6537,6536,6535,6534,6532,6529,6527,6526,6523,6522,6521,6520,6518,6516,6515,6513,6512,6505,6504,6474,6472,6471,6470,6469,6468,6467,6466,6464,6462,6460,6459,6458,6457,6455,6454,6453,6452,6451,6450,6449,6448,6446,5747,5745,5742,5741,5738,5734,5733,5732,5731,5730,5729,5728,5727,5726,5725,5724,5723,5721,5720,5718,5638,5637,5636,5635,5633,5631,5630,5629,5628,5627,5626,5487,5485,5483,5481,5478,5477,5472,5470,5466,5464,5463,5462,5461,5460,5459,5457,5455,5453,5452,5428,5427,5426,5425,5424,5423,5422,5421,5419,5417,5416,5414,5411,5409,5408,5407,5406,5405,5404,5403,5402,5401,5400,5398,5396,4992,4990,4988,4986,4980,4977,4975,4973,4971,4968,4966,4964,4963,4962,4961,4960,4959,4958,4957,4947,4944,4942,4940,4938,4936,4934,4932,4931,4929,4925,4923,4920,4919,4918,4917,4915,4908,4907,4906,4905,4904,4903,4902,4901,4900,4899,4898,4897,4896,4895,4894,4893,4892,4891,4890,4889,4888,4887,4886,4885,4884,4883,4882,4881,4879,4877,4873,4870,4868,4867,4866,4865,4864,4863,4862,4861,4860,4859,4695,4693,4692,4689,4688,4687,4686,4685,4684,4680,4679,4677,4676,4675,4674,4673,4671,4669,4667,4665,4663,4662,4562,4561,4560,4559,4558,4557,4556,4553,4552,4551,4550,4549,4548,4547,4546,4545,4544,4543,4542,4539,4538,4536,4534,4532,4524,4522,4521,4519,4515,4513,4511,4509,4507,4503,4502,4501,4500,4499,4498,4497,4496,4493,4488,4487,4484,4479,4478,4477,4476,4475,4474,4473,4472,4471,4465,4461,4459,4458,4457,4456,4455,4436,4432,4430]
      // for (const memberId of toDelete) {
      //   console.log(`Deleting member ID: ${memberId}`);
      //   const response = await fetch(
      //     `${process.env.REACT_APP_API_URL}/api/upload/mass-upload/${memberId}`,
      //     {
      //       method: "DELETE",
      //       headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      //     }
      //   );
      // }
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/mass-upload/${headId}`,
        {
          method: "DELETE",
          headers: { "x-api-key": process.env.REACT_APP_API_KEY },
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showToast(result.message || "Delete failed.");
        throw new Error(result.message || "Delete failed.");
      }
      showToast("Delete successful!");
      // Optionally, redirect after delete
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      showToast(`Delete failed: ${error.message}`);
    } finally {
      setIsBusy(false);
    }
  }, [headId, navigate, showToast]);

  if (isLoading) {
    return <div style={{ textAlign: 'center', marginTop: '2rem' }}><BusyIndicator active text="Loading details..." /></div>;
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", paddingBottom: "80px", paddingTop: "60px" }}>
      <ui5-toast ref={toastRef}></ui5-toast>
      {isBusy && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <BusyIndicator active text="Saving changes..." />
        </div>
      )}

      {/* Header Bar */}
      <UI5Bar design="Header" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
        <Button icon="nav-back" slot="startContent" onClick={() => navigate(-1)} tooltip="Back to Overview" />
        <Title level="H4" slot="startContent" style={{ marginLeft: '1rem', flexGrow: 1, textAlign: 'left' }}>
            Edit Household: {headerDataFromRoute.first_name || formData.firstName} {headerDataFromRoute.last_name || formData.lastName} (ID: {headId})
        </Title>
         <Text slot="endContent" style={{marginRight: '1rem'}}>
            {headerDataFromRoute.barangay || formData.barangay}, {headerDataFromRoute.municipality || formData.cityMunicipality}
         </Text>
      </UI5Bar>

      {/* Form Content Area - Replicates MainFormContainer's step rendering */}
      <div style={{ padding: '1rem', marginTop: '10px' /* Adjust based on header height */ }}>
        {currentStep === 1 && <ManualForm formData={formData} onFormChange={handleFormChange} setFormData={setFormData} handleNext={handleNext} cityList={cityList} />}
        {currentStep === 2 && <SocioeconomicForm formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 3 && <SocioeconomicForm2 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 4 && <SocioeconomicForm3 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 5 && <SocioeconomicForm4 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 6 && <SocioeconomicForm5 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 7 && <SocioeconomicForm6 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 8 && <SocioeconomicForm7 formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 9 && <EnvironmentSanitationForm formData={formData.environmentSanitation || {}} onFormChange={(updatedData) => handleFormChange("environmentSanitation", updatedData)} handlePrevious={handlePrevious} handleNext={handleNext} />}
        {currentStep === 10 && (
          <FamilyDemographic
            key={formData.familyMembers?.length || 0}
            formData={formData}
            onFormChange={(updatedArray) => handleFormChange("familyMembers", updatedArray)}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            cityList={cityList}
          />
        )}
        {currentStep === 11 && <LastPage formData={formData} onFormChange={handleFormChange} handlePrevious={handlePrevious} />}
      </div>

      {/* Navigation buttons */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          backgroundColor: "#fff",
          borderTop: "1px solid #d9d9d9",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
          zIndex: 100
        }}
      >
        {/* Previous button (left) */}
        <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
          {currentStep > 1 && (
            <Button design="Transparent" onClick={handlePrevious}>
              Previous
            </Button>
          )}
        </div>
        {/* Save Changes and Delete buttons (center) */}
        <div style={{ flex: 2, display: "flex", justifyContent: "center", gap: "1rem" }}>
          <Button design="Positive" onClick={handleUpdate} disabled={isBusy}>
            Save Changes
          </Button>
          <Button design="Negative" onClick={handleDelete} disabled={isBusy}>
            Delete
          </Button>
        </div>
        {/* Next button (right) */}
        <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          {currentStep < TOTAL_STEPS && (
            <Button design="Emphasized" onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </div>

      {/* DEBUG: Show current formData as JSON */}
      {process.env.NODE_ENV === "development" && (
        <div style={{ margin: "2rem 0", padding: "1rem", background: "#f7f7f7", border: "1px solid #ccc", fontSize: "0.9em", maxHeight: 400, overflow: "auto" }}>
          <b>DEBUG: Current formData</b>
          <pre>{JSON.stringify(formData, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default DetailView;
